import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { ValidationErrors, ValidatorFn } from '@angular/forms'
import { FieldTree, form, required } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { DateService } from '@lemon/core/service/date.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { arvonMuutokset, nollaa } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { KirjanpitajanNimitiedot, KirjanpitajanTiimi } from 'app/_jaettu-lemonator/model/kirjanpitaja'
import { AsiakkaanTilastojenPaivitysTyojonotiedot } from 'app/_jaettu-lemonator/model/tyojono'
import { KirjanpitajanTiimiService } from 'app/_jaettu-lemonator/service/kirjanpitajan-tiimi.service'
import { Timestamp } from 'firebase/firestore'
import { firstValueFrom, from, map, Observable, of, Subject, switchMap, takeUntil, timer } from 'rxjs'

interface TilastonSiirtoLomake {
  vanhaKirjanpitaja: string | null
  vanhaTiimi: KirjanpitajanTiimi['avain'] | null
  uusiKirjanpitaja: string | null
  uusiTiimi: KirjanpitajanTiimi['avain'] | null
  pvmAlkaen: Date | null
  pvmPaattyen: Date | null
}

const TYHJA_LOMAKE: TilastonSiirtoLomake = { vanhaKirjanpitaja: null, vanhaTiimi: null, uusiKirjanpitaja: null, uusiTiimi: null, pvmAlkaen: null, pvmPaattyen: null }

@Component({
  selector: 'app-tilaston-siirto-tyokalu',
  templateUrl: './tilaston-siirto-tyokalu.component.html',
  styleUrls: ['./tilaston-siirto-tyokalu.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TilastonSiirtoTyokaluComponent implements OnInit, OnDestroy {
  lomake: FieldTree<TilastonSiirtoLomake>
  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)

  kirjanpitajienNimitiedotObservable: Observable<KirjanpitajanNimitiedot[]>
  uudenKirjanpitajanTiimitObservable: Observable<KirjanpitajanTiimi[]>
  vanhanKirjanpitajanTiimitObservable: Observable<KirjanpitajanTiimi[]>

  readonly siirtoOnnistui = signal(false)
  readonly siirtoVirhe = signal<string | null>(null)
  readonly siirtoKaynnissa = signal(false)

  private _buttonPushInflight: boolean = false

  constructor(
    private _kirjanpitajaService: KirjanpitajaService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _validationService: FormValidationService,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _firebaseLemonator: FirebaseLemonator,
    private _laskuUriService: LaskuUriService,
    private _kirjanpitajanTiimiService: KirjanpitajanTiimiService,
    private _dialog: MatDialog,
    private _dateService: DateService,
  ) {}

  ngOnInit() {
    this._alustaForm()

    this.kirjanpitajienNimitiedotObservable = this._kirjanpitajaService.kirjanpitajienNimitiedotObservable.pipe(
      map(kirjanpitajat => {
        if (kirjanpitajat?.length) {
          return [...kirjanpitajat].sort((a, b) => {
            if (a.etunimi !== b.etunimi) {
              return a.etunimi.localeCompare(b.etunimi)
            }
            return a.sukunimi.localeCompare(b.sukunimi)
          })
        }
        return []
      }),
      takeUntil(this._ngUnsubscribe),
    )
  }

  ngOnDestroy(): void {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  private _alustaForm(): void {
    const injector = this._injector
    this.lomake = form(
      signal<TilastonSiirtoLomake>(TYHJA_LOMAKE),
      polku => {
        required(polku.vanhaKirjanpitaja)
        required(polku.uusiKirjanpitaja)
        required(polku.uusiTiimi)
        validoiReaktiivisesti(polku.pvmAlkaen, this._paivamaaraValidoija)
        validoiReaktiivisesti(polku.pvmPaattyen, this._paivamaaraValidoija)
      },
      { injector },
    )

    this.uudenKirjanpitajanTiimitObservable = arvonMuutokset(this.lomake.uusiKirjanpitaja, { injector }).pipe(
      switchMap(kirjanpitajaAvain => {
        if (!kirjanpitajaAvain) {
          return of([])
        }
        return from(this._etsiKirjanpitajanTiimit(kirjanpitajaAvain))
      }),
      takeUntil(this._ngUnsubscribe),
    )

    this.vanhanKirjanpitajanTiimitObservable = arvonMuutokset(this.lomake.vanhaKirjanpitaja, { injector }).pipe(
      switchMap(kirjanpitajaAvain => {
        if (!kirjanpitajaAvain) {
          return of([])
        }
        return from(this._etsiKirjanpitajanTiimit(kirjanpitajaAvain))
      }),
      takeUntil(this._ngUnsubscribe),
    )

    arvonMuutokset(this.lomake.uusiKirjanpitaja, { injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => {
        nollaa(this.lomake.uusiTiimi, null)
      })

    arvonMuutokset(this.lomake.vanhaKirjanpitaja, { injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => {
        nollaa(this.lomake.vanhaTiimi, null)
      })
  }

  private async _etsiKirjanpitajanTiimit(kirjanpitajaAvain: string): Promise<KirjanpitajanTiimi[]> {
    const kaikkiTiimit = await this._kirjanpitajanTiimiService.annaKaikkiTiimit()
    return kaikkiTiimit.filter(tiimi => tiimi.esihenkilot.some(e => e.kirjanpitajaAvain === kirjanpitajaAvain) || tiimi.jasenet.some(j => j.kirjanpitajaAvain === kirjanpitajaAvain))
  }

  async suoritaSiirto(): Promise<void> {
    if (this.lomake().invalid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    if (this._buttonPushInflight) {
      return
    }
    this._buttonPushInflight = true

    const areYouSureData: AreYouSureDialogData = {
      rightAction: 'Vahvista siirto',
      header: 'Vahvista tilastotietojen siirto',
      text: 'Tilastotiedot siirretään valitulle kirjanpitäjälle ja tiimille. Oletko varma?',
    }

    try {
      const isSure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: areYouSureData }).afterClosed())
      if (!isSure) {
        this._buttonPushInflight = false
        return
      }
    } catch (err) {
      this._errorHandler.handleError(err)
      this._buttonPushInflight = false
      return
    }

    this.siirtoKaynnissa.set(true)
    this.siirtoOnnistui.set(false)
    this.siirtoVirhe.set(null)

    this._ladataanService.aloitaLataaminen()

    try {
      const vanhaKirjanpitajaAvain = this.lomake.vanhaKirjanpitaja().value()
      const vanhaTiimiAvain = this.lomake.vanhaTiimi().value()
      const uusiKirjanpitajaAvain = this.lomake.uusiKirjanpitaja().value()
      const uusiTiimiAvain = this.lomake.uusiTiimi().value()
      const pvmAlkaen = this.lomake.pvmAlkaen().value()
      const pvmPaattyen = this.lomake.pvmPaattyen().value()

      const kirjautunutKayttaja = await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()

      const tyojonoAvain = this._firebaseLemonator.firestoreCreateId()
      const tilastojenPaivitysUri = this._laskuUriService.getTilastojenPaivitysTyojonoUri('kaikki', tyojonoAvain)

      const data: AsiakkaanTilastojenPaivitysTyojonotiedot = {
        asiakasAvain: null,
        vanhaKirjanpitajaAvain,
        uusiKirjanpitajaAvain,
        uusiKirjanpitajanTiimi: uusiTiimiAvain,
        paivittaja: kirjautunutKayttaja.uid,
        created: Timestamp.now(),
      }

      if (vanhaTiimiAvain) {
        data.vanhaKirjanpitajanTiimi = vanhaTiimiAvain
      }

      if (pvmAlkaen) {
        data.pvmAlkaen = this._dateService.dateToLocalDate(pvmAlkaen)
      }

      if (pvmPaattyen) {
        data.pvmPaattyen = this._dateService.dateToLocalDate(pvmPaattyen)
      }

      const batch = this._firebaseLemonator.firestoreBatch()
      batch.set(tilastojenPaivitysUri, data)

      await batch.commit()

      this.siirtoOnnistui.set(true)
      timer(8000)
        .pipe(takeUntil(this._ngUnsubscribe))
        .subscribe(() => {
          nollaa(this.lomake, TYHJA_LOMAKE)
          this.siirtoOnnistui.set(false)
        })
    } catch (err) {
      this._errorHandler.handleError(err)
      this.siirtoVirhe.set('Tilastotietojen siirto epäonnistui. Yritä uudelleen.')
    } finally {
      this.siirtoKaynnissa.set(false)
      this._buttonPushInflight = false
      this._ladataanService.lopetaLataaminen()
    }
  }

  private _paivamaaraValidoija: ValidatorFn = (): ValidationErrors | null => {
    const pvmAlkaen = this.lomake?.pvmAlkaen().value()
    const pvmPaattyen = this.lomake?.pvmPaattyen().value()

    if (pvmAlkaen && pvmPaattyen && pvmAlkaen > pvmPaattyen) {
      return { paivamaaraVirhe: true }
    }
    return null
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  nollaaLomake(): void {
    nollaa(this.lomake, TYHJA_LOMAKE)
    this.siirtoOnnistui.set(false)
    this.siirtoVirhe.set(null)
  }
}
